import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { staffApi, ApiError, type QuoteListResponse } from "@/api";
import type { Quote, QuoteStatus } from "@/types";
import { QUOTE_STATUSES } from "@/types";
import { StatusPill, Spinner, SelectField } from "@/components/ui";
import { statusLabel } from "@/lib/status";
import { formatMoney } from "@/lib/money";
import { formatDateShort, contactName } from "@/lib/format";
import "./quotes-list.css";

function unwrap(res: QuoteListResponse | Quote[]): Quote[] {
  return Array.isArray(res) ? res : res.items ?? [];
}

/** Same client-side matcher as the leads board/list (lib/leads matchesQuery),
 *  over the quote's fields — plus the quote number and the site address, the
 *  way staff find these ("27 Rowan" should land on the quote for 27 Rowan Drive). */
function matchesQuery(quote: Quote, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    quote.quote_number,
    quote.contact_first_name,
    quote.contact_last_name,
    quote.company_name,
    quote.email,
    quote.site_address_line_1,
    quote.site_address_line_2,
    quote.site_address_line_3,
    quote.site_town,
    quote.site_postcode,
    quote.project_description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

type QuotesView = "all" | "open";

const VIEWS: { value: QuotesView; label: string }[] = [
  { value: "all", label: "All" },
  { value: "open", label: "Open only" },
];

// Survives list → quote → back (the list unmounts on navigation) but resets
// to "All" in a fresh tab, per the spec.
const VIEW_STORAGE_KEY = "quotes-list.view";

function storedView(): QuotesView {
  return sessionStorage.getItem(VIEW_STORAGE_KEY) === "open" ? "open" : "all";
}

export function QuotesList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<"" | QuoteStatus>("");
  const [view, setView] = useState<QuotesView>(storedView);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  const visible = useMemo(() => quotes.filter((quote) => matchesQuery(quote, q)), [quotes, q]);

  function selectView(v: QuotesView) {
    setView(v);
    sessionStorage.setItem(VIEW_STORAGE_KEY, v);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    staffApi
      .listQuotes({
        status: statusFilter || undefined,
        open_only: view === "open" || undefined,
        per_page: 100,
      })
      .then((res) => {
        if (!cancelled) setQuotes(unwrap(res));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load quotes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, view]);

  // The header's "New quote" routes here with { create: true } — start a blank
  // draft (clear the flag first so a refresh/back doesn't re-fire it).
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      navigate(location.pathname, { replace: true, state: null });
      void createDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  async function createDraft() {
    if (creating) return;
    setCreating(true);
    try {
      const q = await staffApi.createQuote({});
      navigate(`/staff/quotes/${q.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create a draft.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <section className="quotes-list">
      <header className="quotes-list__head">
        <div>
          <h1>Quotes</h1>
          <p className="quotes-list__count">{visible.length} shown</p>
        </div>
      </header>

      <div className="quotes-list__filters">
        <SelectField
          label="Status"
          labelHidden
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "" | QuoteStatus)}
        >
          <option value="">All statuses</option>
          {QUOTE_STATUSES.map((s) => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </SelectField>
        <div className="quotes-list__seg" role="tablist" aria-label="Open filter">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              type="button"
              role="tab"
              aria-selected={view === v.value}
              className={`quotes-list__seg-btn${view === v.value ? " is-on" : ""}`}
              onClick={() => selectView(v.value)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <input
          className="quotes-list__search"
          type="search"
          placeholder="Search quote number, client, email, address, postcode…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {error ? <p className="quotes-list__error" role="alert">{error}</p> : null}

      {loading ? (
        <div className="quotes-list__loading"><Spinner /></div>
      ) : visible.length === 0 ? (
        <p className="quotes-list__empty">No quotes match these filters.</p>
      ) : (
        <div className="quotes-table" role="table" aria-label="Quotes">
          <div className="quotes-table__head" role="row">
            <span role="columnheader">Quote</span>
            <span role="columnheader">Client</span>
            <span role="columnheader">Status</span>
            <span role="columnheader" className="num">Value</span>
            <span role="columnheader">Expiry</span>
            <span role="columnheader" className="num">Views</span>
          </div>
          {visible.map((quote) => (
            <Link
              key={quote.id}
              to={`/staff/quotes/${quote.id}`}
              className="quotes-table__row"
              role="row"
            >
              <span role="cell" data-label="Quote" className="quotes-table__ref numeric">
                {quote.quote_number}
              </span>
              <span role="cell" data-label="Client">{contactName(quote)}</span>
              <span role="cell" data-label="Status"><StatusPill status={quote.status} /></span>
              <span role="cell" data-label="Value" className="num numeric">
                {quote.quote_value != null ? formatMoney(quote.quote_value, quote.currency) : "—"}
              </span>
              <span role="cell" data-label="Expiry">{formatDateShort(quote.expiry_date)}</span>
              <span role="cell" data-label="Views" className="num numeric">{quote.view_count ?? 0}</span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
